import { useAuth } from "./use-auth";
import { useCurrentUser } from "./use-current-user";
import PremiumFeatureGate from "../components/common/premium/PremiumFeatureGate";

// Matches the permissions in the UserPermissions from BE
export const UserPermissions = {
  TEAMS_ENABLED: "scribenote:teams",
  BASIC_TEMPLATES_ENABLED: "scribenote:basic_templates",
  ADAPTIVE_TEMPLATES_ENABLED: "scribenote:adaptive_templates",
  CUSTOM_DEFAULTS_ENABLED: "scribenote:custom_defaults",
  CLIENT_SUMMARY_ENABLED: "scribenote:client_summary",
  WRITING_ASSISTANT_ENABLED: "scribenote:writing_assistant",
  MULTILINGUAL_ENABLED: "scribenote:multilingual",
  POINT_FORM_AP_FIELDS_ENABLED: "scribenote:point_form_ap_fields",
  NAME_CORRECTION_ENABLED: "scribenote:name_correction",
  DESKTOP_APP_ENABLED: "scribenote:desktop_app",
  PREMIUM_TRANSCRIPT_ENABLED: "scribenote:premium_transcript",
  PREMIUM_DEFAULT_NOTES_ENABLED: "scribenote:premium_default_notes",
  HUMAN_SUPPORT_ENABLED: "scribenote:human_support",
};

export function usePremiumFeature() {
  const { account } = useAuth();
  const { data: userData } = useCurrentUser();
  const userPermissions = userData?.user?.userPermissions || [];
  const hasPaidSubscription = account?.isSubscribed || false;

  const hasPermission = (permissionName) => {
    return userPermissions.some(
      (permission) =>
        permission.permissionName === permissionName &&
        permission.isEnabled,
    );
  };

  const hasFeatureAccess = (featurePermission) => {
    // If no specific permission provided, fall back to subscription check
    if (!featurePermission) {
      return hasPaidSubscription;
    }

    return hasPermission(featurePermission);
  };

  const gateComponentIfNeeded = (Component, options) => {
    if (!hasFeatureAccess(options?.requiredPermission)) {
      const GatedComponent = (props) => (
        <PremiumFeatureGate
          featureName={options?.featureName}
          iconPosition={options?.iconPosition}
          iconSize={options?.iconSize}
          requiredPermission={options?.requiredPermission}
        >
          <Component {...props} />
        </PremiumFeatureGate>
      );
      GatedComponent.displayName = `PremiumFeatureGate(${
        Component.displayName || Component.name || "Component"
      })`;
      return GatedComponent;
    }

    return Component;
  };

  const renderPremiumContent = ({
    premiumContent,
    freeContent = null,
    gateOptions,
    requiredPermission,
  }) => {
    if (hasFeatureAccess(requiredPermission)) {
      return premiumContent;
    }

    if (freeContent) {
      return freeContent;
    }

    return (
      <PremiumFeatureGate
        featureName={gateOptions.featureName}
        iconPosition={gateOptions.iconPosition}
        requiredPermission={requiredPermission}
      >
        {premiumContent}
      </PremiumFeatureGate>
    );
  };

  return {
    hasPaidSubscription,
    hasPermission,
    hasFeatureAccess,
    gateComponentIfNeeded,
    renderPremiumContent,
    PremiumFeatureGate,
    UserPermissions,
  };
}
